import React, { useState, useRef } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const colors = {
  lobbyQueue: '#7A9EAF',
  campaignQueue: '#ECBE55',
  droppedQueue: '#ECBE55',
  alert: '#C74D2D',
  accent: '#FCF2D8',
  pageBackground: '#0F172A',
  panelBackground: '#1E293B',
  chartBackground: '#1F2937',
  border: '#374151',
  divider: '#4B5563',
  textPrimary: '#FCF2D8',
  textSecondary: '#D1D5DB',
  textMuted: '#9CA3AF',
  buttonPrimary: '#7A9EAF',
  buttonDanger: '#C74D2D',
  buttonSuccess: '#ECBE55',
  buttonDisabled: '#4B5563',
  inputBackground: '#1E293B',
};

const TwilioSimDashboard = () => {
  const [config, setConfig] = useState({
    n_groups: 10,
    resource_group_size: 5,
    asmtm_arr: 0.5,
    asmtm_c_conseq: 4.0,
    asmtm_time_pattern: 'social',
    att_percentage: 30,
    tmobile_percentage: 40,
    verizon_percentage: 30,
    tmobile_mps: 4,
    att_mps: 4,
    verizon_mps: 4,
    tmobile_daily_limit: 2000,
  });

  const [simState, setSimState] = useState({
    phase: 'idle',
    data: null,
    frame: 0,
    isPlaying: false,
  });

  const playbackIntervalRef = useRef(null);

  const buildSimulation = async () => {
    // Validate carrier distribution
    const totalPercentage = config.att_percentage + config.tmobile_percentage + config.verizon_percentage;
    if (totalPercentage !== 100) {
      alert(`Carrier percentages must total 100% (currently ${totalPercentage}%)`);
      return;
    }
    
    setSimState({ phase: 'building', data: null, frame: 0, isPlaying: false });

    try {
      const backendConfig = {
        n_groups: config.n_groups,
        resource_group_size: config.resource_group_size,
        asmtm_arr: config.asmtm_arr,
        asmtm_c_conseq: config.asmtm_c_conseq,
        asmtm_time_pattern: config.asmtm_time_pattern,
        att_percentage: config.att_percentage,
        tmobile_percentage: config.tmobile_percentage,
        verizon_percentage: config.verizon_percentage,
        tmobile_mps: config.tmobile_mps,
        att_mps: config.att_mps,
        verizon_mps: config.verizon_mps,
        tmobile_daily_limit: config.tmobile_daily_limit === 'no_limit' ? -1 : parseInt(config.tmobile_daily_limit),
      };
      
      const response = await fetch('/api/simulate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backendConfig),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('✅ Simulation built');
      console.log('📊 ASMTM Results:', data.asmtm);

      setSimState({
        phase: 'ready',
        data: data,
        frame: 0,
        isPlaying: false,
      });

    } catch (error) {
      console.error('❌ Build failed:', error);
      setSimState({ phase: 'idle', data: null, frame: 0, isPlaying: false });
      alert(`Build failed: ${error.message}`);
    }
  };

  const downloadCSV = () => {
    if (!simState.data) return;

    const { config: cfg, asmtm, timeline, metrics } = simState.data;

    // Build comprehensive CSV
    const rows = [
      // Configuration
      ['CONFIGURATION'],
      ['Parameter', 'Value'],
      ['N_groups (SMS/MMS Groups)', cfg.n_groups],
      ['N_people (People per Group)', cfg.n_people],
      ['ARR (Agent Response Rate)', cfg.arr],
      ['C_conseq (Contact Rate)', cfg.c_conseq],
      ['Time Pattern', cfg.time_pattern],
      [],
      ['--- CARRIER DISTRIBUTION ---'],
      ['AT&T Percentage', `${cfg.att_percentage}%`],
      ['T-Mobile Percentage', `${cfg.tmobile_percentage}%`],
      ['Verizon Percentage', `${cfg.verizon_percentage}%`],
      [],
      ['--- DAILY MESSAGE LIMITS ---'],
      ['AT&T Daily Limit', 'N/A'],
      ['T-Mobile Daily Limit', cfg.tmobile_daily_limit === -1 ? 'No limit' : cfg.tmobile_daily_limit.toLocaleString()],
      ['Verizon Daily Limit', 'N/A'],
      [],
      ['--- BRAND MPS LIMITS ---'],
      ['AT&T MPS Limit', cfg.att_mps],
      ['T-Mobile MPS Limit', cfg.tmobile_mps],
      ['Verizon MPS Limit', cfg.verizon_mps],
      ['Total Brand MPS', cfg.total_brand_mps],
      [],
      
      // ASMTM Calculations
      ['ASMTM MODEL CALCULATIONS'],
      ['Metric', 'Value'],
      ['Formula', asmtm.formula],
      [],
      ['--- PER GROUP ---'],
      ['Agent Messages (per group)', asmtm.per_group.agent_messages],
      ['Human Messages (per group)', asmtm.per_group.human_messages],
      ['Total Messages (per group)', asmtm.per_group.total_messages],
      [],
      ['--- ALL GROUPS ---'],
      ['Total Agent Messages', asmtm.total_agent_messages],
      ['Total Human Messages', asmtm.total_human_messages],
      ['Total Messages (Agent + Human)', asmtm.total_messages],
      [],
      ['--- CARRIER DISTRIBUTION ---'],
      ['AT&T Messages', metrics.total_att_messages],
      ['T-Mobile Messages', metrics.total_tmobile_messages],
      ['Verizon Messages', metrics.total_verizon_messages],
      [],
      ['Peak Hour', asmtm.peak_hour],
      ['Peak Hourly Rate', asmtm.peak_hourly_rate],
      [],
      
      // Hourly Rates
      ['HOURLY MESSAGE RATES (24 hours - scaled by N_groups)'],
      ['Hour', 'Agent Messages/Hour', 'Human Messages/Hour'],
      ...asmtm.agent_hourly_rates.map((agent, idx) => [
        idx,
        agent,
        asmtm.human_hourly_rates[idx]
      ]),
      [],
      
      // Queue Metrics
      ['QUEUE METRICS'],
      ['Metric', 'Value'],
      ['Peak Brand Queue', metrics.peak_brand_queue],
      ['Final Brand Queue', metrics.final_brand_queue],
      ['Brand Bottleneck', metrics.brand_bottleneck ? 'Yes' : 'No'],
      ['Peak T-Mobile Daily Limit Queue', metrics.peak_tmobile_daily_limit_queue],
      ['Final T-Mobile Daily Limit Queue', metrics.final_tmobile_daily_limit_queue],
      ['Peak US 10DLC Dropped Queue', metrics.peak_us_10dlc_dropped_queue],
      ['Final US 10DLC Dropped Queue', metrics.final_us_10dlc_dropped_queue],
      ['Total T-Mobile Messages Processed', metrics.total_messages_processed_tmobile],
      ['Total Messages Blocked', metrics.total_messages_blocked],
      ['Total Messages Dropped (US 10DLC)', metrics.total_messages_dropped],
      ['Daily Limit Hit At', metrics.daily_limit_hit_at || 'N/A'],
      [],
      
      // Timeline Data
      ['QUEUE SIMULATION TIMELINE (1440 snapshots - 1 per minute)'],
      ['Time', 'Hour', 'Brand MPS', 'T-Mobile Daily Limit', 'US 10DLC Dropped', 'AT&T Total', 'T-Mobile Total', 'Verizon Total', 'Msgs Processed', 'Msgs Blocked', 'Msgs Dropped'],
      ...timeline.map(t => [
        t.time,
        t.hour,
        t.brand_queue,
        t.tmobile_daily_limit_queue,
        t.us_10dlc_dropped_queue,
        t.total_att_messages,
        t.total_tmobile_messages,
        t.total_verizon_messages,
        t.msgs_processed_brand,
        t.msgs_blocked_by_limit,
        t.msgs_dropped_this_minute
      ])
    ];

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `simulation_${cfg.time_pattern}_g${cfg.n_groups}_n${cfg.n_people}_arr${cfg.arr}_${timestamp}.csv`;
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`📥 Downloaded: ${filename}`);
  };

  const playSimulation = () => {
    if (!simState.data?.timeline) return;

    setSimState(prev => ({ ...prev, phase: 'playing', frame: 0, isPlaying: true }));

    const totalFrames = simState.data.timeline.length;  // 1440 frames
    const playbackDurationSeconds = 60;
    const fps = totalFrames / playbackDurationSeconds;  // 24 FPS
    const msPerFrame = 1000 / fps;  // ~41.7ms per frame

    console.log(`▶️ Playing ${totalFrames} frames over ${playbackDurationSeconds}s at ${fps.toFixed(2)} FPS`);

    playbackIntervalRef.current = setInterval(() => {
      setSimState(prev => {
        const nextFrame = prev.frame + 1;

        if (nextFrame >= prev.data.timeline.length) {
          clearInterval(playbackIntervalRef.current);
          console.log('✅ Playback completed');
          return { ...prev, phase: 'completed', frame: prev.frame, isPlaying: false };
        }

        return { ...prev, frame: nextFrame };
      });
    }, msPerFrame);
  };

  const stopPlayback = () => {
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
    }
    setSimState(prev => ({ ...prev, isPlaying: false }));
  };

  const resetSimulation = () => {
    stopPlayback();
    setSimState({ phase: 'idle', data: null, frame: 0, isPlaying: false });
  };

  const isConfigLocked = simState.phase !== 'idle';

  const getCurrentData = () => {
    if (!simState.data?.timeline) return [];
    
    // Show all data when completed, otherwise show up to current frame
    if (simState.phase === 'completed') {
      return simState.data.timeline;  // All 1440 points
    }
    
    return simState.data.timeline.slice(0, simState.frame + 1);
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    color: colors.textPrimary,
    fontSize: '14px',
  };

  const buttonStyle = (bgColor, disabled = false) => ({
    padding: '10px 20px',
    backgroundColor: disabled ? colors.buttonDisabled : bgColor,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  return (
    <div style={{ backgroundColor: colors.pageBackground, minHeight: '100vh', padding: '20px', fontFamily: 'Inter, system-ui, sans-serif', color: colors.textPrimary }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: '700', color: colors.accent }}>
          Outbound Message Traffic Simulation
        </h1>
        <p style={{ margin: 0, color: colors.textSecondary, fontSize: '14px' }}>
          ASMTM Multi-Group Model • Pattern: {config.asmtm_time_pattern}
        </p>
      </div>

      {/* Status Panel */}
      {simState.data && (
        <div style={{ 
          backgroundColor: colors.panelBackground, 
          border: `1px solid ${colors.border}`, 
          borderRadius: '8px', 
          padding: '16px', 
          marginBottom: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>Groups</div>
            <div style={{ color: colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>{simState.data.config.n_groups}</div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>People per Group</div>
            <div style={{ color: colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>{simState.data.config.n_people}</div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>Human Messages</div>
            <div style={{ color: colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>{simState.data.asmtm.total_human_messages.toLocaleString()}<span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '4px' }}>msgs/day</span></div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>Agent Messages</div>
            <div style={{ color: colors.accent, fontSize: '20px', fontWeight: '600' }}>{simState.data.asmtm.total_agent_messages.toLocaleString()}<span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '4px' }}>msgs/day</span></div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '4px' }}>Total Messages</div>
            <div style={{ color: colors.textPrimary, fontSize: '20px', fontWeight: '600' }}>{simState.data.asmtm.total_messages.toLocaleString()}<span style={{ fontSize: '12px', color: colors.textMuted, marginLeft: '4px' }}>msgs/day</span></div>
          </div>
        </div>
      )}

      {/* Main Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '20px', minHeight: '600px' }}>
        
        {/* Left Panel - Configuration */}
        <div style={{ backgroundColor: colors.panelBackground, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: colors.textPrimary, borderBottom: `2px solid ${colors.divider}`, paddingBottom: '10px' }}>
            Configuration
          </h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.accent, marginBottom: '12px', textTransform: 'uppercase' }}>
                Simulation Configs
              </h3>
              
              <h4 style={{ fontSize: '12px', fontWeight: '700', color: colors.textPrimary, marginBottom: '12px', marginTop: '0' }}>
                Activity Configs
              </h4>
              
              {/* Number of SMS/MMS Groups */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Number of SMS/MMS Groups (1-100)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={config.n_groups}
                  onChange={(e) => setConfig({...config, n_groups: parseInt(e.target.value) || 10})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              {/* People per Group */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  People per Group (2-10)
                </label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={config.resource_group_size}
                  onChange={(e) => setConfig({...config, resource_group_size: parseInt(e.target.value) || 5})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Agent Response Rate (0.0-1.0)
                </label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.asmtm_arr}
                  onChange={(e) => setConfig({...config, asmtm_arr: parseFloat(e.target.value) || 0.5})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Contact Rate (msgs/person/day)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={config.asmtm_c_conseq}
                  onChange={(e) => setConfig({...config, asmtm_c_conseq: parseFloat(e.target.value) || 4.0})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Time Pattern
                </label>
                <select
                  value={config.asmtm_time_pattern}
                  onChange={(e) => setConfig({...config, asmtm_time_pattern: e.target.value})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                >
                  <option value="social">Social</option>
                  <option value="work">Work</option>
                  <option value="uniform">Uniform</option>
                  <option value="night">Night</option>
                </select>
              </div>
              
              <h4 style={{ fontSize: '12px', fontWeight: '600', color: colors.textSecondary, marginBottom: '8px', marginTop: '16px' }}>
                Carrier Message Distribution
              </h4>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  AT&T (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.att_percentage}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 100) {
                      setConfig({...config, att_percentage: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  T-Mobile (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.tmobile_percentage}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 100) {
                      setConfig({...config, tmobile_percentage: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Verizon (%)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.verizon_percentage}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 100) {
                      setConfig({...config, verizon_percentage: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>
              
              {/* Validation Indicator */}
              {(() => {
                const total = config.att_percentage + config.tmobile_percentage + config.verizon_percentage;
                const isValid = total === 100;
                const bgColor = isValid ? colors.success : colors.alert;
                
                return (
                  <div style={{ 
                    backgroundColor: bgColor,
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '8px',
                    textAlign: 'center',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Total: {total}% {isValid ? '✓' : '⚠ Must equal 100%'}
                  </div>
                );
              })()}
            </div>

            <div>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.accent, marginBottom: '12px', textTransform: 'uppercase' }}>
                Daily Message Limits
              </h3>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  AT&T
                </label>
                <input
                  type="text"
                  value="N/A"
                  disabled
                  style={{...inputStyle, opacity: 0.6, cursor: 'not-allowed'}}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  T-Mobile
                </label>
                <select
                  value={config.tmobile_daily_limit}
                  onChange={(e) => setConfig({...config, tmobile_daily_limit: e.target.value})}
                  disabled={isConfigLocked}
                  style={inputStyle}
                >
                  <option value="2000">2,000</option>
                  <option value="10000">10,000</option>
                  <option value="40000">40,000</option>
                  <option value="200000">200,000</option>
                  <option value="no_limit">No limit</option>
                </select>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Verizon
                </label>
                <input
                  type="text"
                  value="N/A"
                  disabled
                  style={{...inputStyle, opacity: 0.6, cursor: 'not-allowed'}}
                />
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.accent, marginBottom: '12px', textTransform: 'uppercase' }}>
                Throughput (MPS)
              </h3>
              
              <h4 style={{ fontSize: '12px', fontWeight: '600', color: colors.textSecondary, marginBottom: '8px', marginTop: '8px' }}>
                Brand MPS Limit
              </h4>
              
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  AT&T MPS Limit (0-500)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.att_mps}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 500) {
                      setConfig({...config, att_mps: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  T-Mobile MPS Limit (0-500)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.tmobile_mps}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 500) {
                      setConfig({...config, tmobile_mps: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: colors.textSecondary, fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                  Verizon MPS Limit (0-500)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="3"
                  value={config.verizon_mps}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    const num = val === '' ? 0 : parseInt(val);
                    if (num <= 500) {
                      setConfig({...config, verizon_mps: num});
                    }
                  }}
                  disabled={isConfigLocked}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
              <button 
                onClick={buildSimulation}
                disabled={isConfigLocked}
                style={buttonStyle(colors.buttonPrimary, isConfigLocked)}
              >
                {simState.phase === 'building' ? 'Building...' : 'Build Simulation'}
              </button>
              
              {simState.phase === 'ready' && (
                <>
                  <button onClick={playSimulation} style={buttonStyle(colors.buttonSuccess)}>
                    ▶ Play (60 seconds)
                  </button>
                  <button onClick={downloadCSV} style={buttonStyle(colors.buttonPrimary)}>
                    📥 Download CSV
                  </button>
                </>
              )}
              
              {simState.phase === 'playing' && (
                <>
                  <button onClick={stopPlayback} style={buttonStyle(colors.buttonDanger)}>
                    ⏸ Pause
                  </button>
                  <button onClick={downloadCSV} style={buttonStyle(colors.buttonPrimary)}>
                    📥 Download CSV
                  </button>
                </>
              )}
              
              {simState.phase === 'completed' && (
                <button onClick={downloadCSV} style={buttonStyle(colors.buttonPrimary)}>
                  📥 Download CSV
                </button>
              )}
              
              {(simState.phase === 'ready' || simState.phase === 'playing' || simState.phase === 'completed') && (
                <button onClick={resetSimulation} style={buttonStyle(colors.alert)}>
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Visualization */}
        <div style={{ backgroundColor: colors.panelBackground, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: colors.textPrimary, borderBottom: `2px solid ${colors.divider}`, paddingBottom: '10px' }}>
            SMS/MMS Processing Queues Behavior
          </h2>
          
          {simState.phase === 'idle' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: colors.textMuted }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <div style={{ fontSize: '16px' }}>Configure parameters and click "Build Simulation"</div>
              </div>
            </div>
          )}
          
          {simState.phase === 'building' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: colors.textMuted }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
                <div style={{ fontSize: '16px' }}>Building simulation...</div>
              </div>
            </div>
          )}
          
          {(simState.phase === 'ready' || simState.phase === 'playing' || simState.phase === 'completed') && simState.data && (
            <div>
              <div style={{ marginBottom: '16px', color: colors.textSecondary, fontSize: '14px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Frame {simState.frame + 1} of {simState.data.timeline?.length || 0}</span>
                <span>{simState.data.timeline[simState.frame]?.time || '0:00'}</span>
              </div>
              
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={getCurrentData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.divider} />
                  <XAxis 
                    dataKey="hour" 
                    type="number"
                    domain={[0, 24]}
                    ticks={[0, 4, 8, 12, 16, 20, 24]}
                    tickFormatter={(hour) => {
                      if (hour === 0 || hour === 24) return '12 AM';
                      if (hour === 12) return '12 PM';
                      if (hour < 12) return `${hour} AM`;
                      return `${hour - 12} PM`;
                    }}
                    allowDataOverflow={true}
                    label={{ value: 'Time of Day', position: 'insideBottom', offset: -5, fill: colors.textSecondary }}
                    stroke={colors.textSecondary}
                  />
                  <YAxis 
                    label={{ value: 'Queue Size', angle: -90, position: 'insideLeft', fill: colors.textSecondary }}
                    stroke={colors.textSecondary} 
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: colors.chartBackground, border: `1px solid ${colors.border}`, borderRadius: '4px' }}
                    labelStyle={{ color: colors.textPrimary }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    formatter={(value) => <span style={{ fontWeight: '700' }}>{value}</span>}
                  />
                  
                  {/* Stacked areas - Area order determines legend order */}
                  <Area 
                    type="monotone" 
                    dataKey="brand_queue" 
                    stackId="1"
                    stroke={colors.lobbyQueue}
                    fill={colors.lobbyQueue}
                    fillOpacity={0.6}
                    name="Brand MPS"
                    isAnimationActive={false}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="tmobile_daily_limit_queue" 
                    stackId="1"
                    stroke={colors.alert}
                    fill={colors.alert}
                    fillOpacity={0.6}
                    name="T-Mobile Daily Limit"
                    isAnimationActive={false}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="us_10dlc_dropped_queue" 
                    stackId="1"
                    stroke={colors.droppedQueue}
                    fill={colors.droppedQueue}
                    fillOpacity={0.6}
                    name="Exceeds Carrier Limits /Dropped"
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
              
              {/* Queue Status Widgets (no title - belongs to chart section) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '20px' }}>
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Brand MPS</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].brand_queue.toLocaleString() 
                      : '0'}
                  </div>
                </div>
                
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Exceeds T-Mobile Daily Limit</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].tmobile_daily_limit_queue.toLocaleString() 
                      : '0'}
                  </div>
                </div>
                
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Exceeds Carrier Limits/Dropped</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].us_10dlc_dropped_queue.toLocaleString() 
                      : '0'}
                  </div>
                </div>
              </div>
              
              {/* Simulation Key Metrics Section */}
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: colors.textPrimary, 
                marginTop: '24px',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Simulation Key Metrics
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                {/* Total Unprocessed Messages */}
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Total Unprocessed Messages</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 ? (() => {
                      const lastFrame = getCurrentData()[getCurrentData().length - 1];
                      const total = (lastFrame.brand_queue || 0) + 
                                    (lastFrame.tmobile_daily_limit_queue || 0) + 
                                    (lastFrame.us_10dlc_dropped_queue || 0);
                      return total.toLocaleString();
                    })() : '0'}
                  </div>
                </div>
                
                {/* Agent Message Costs */}
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Outbound Agent Message Costs</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 ? (() => {
                      const lastFrame = getCurrentData()[getCurrentData().length - 1];
                      const cost = lastFrame.agent_message_cost || 0;
                      return '$' + cost.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      });
                    })() : '$0.00'}
                  </div>
                </div>
              </div>
              
              {/* Carrier Distribution Title */}
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: colors.textPrimary, 
                marginTop: '24px',
                marginBottom: '12px',
                textAlign: 'center'
              }}>
                Agent Message Segments Sent to Carrier
              </h3>
              
              {/* Carrier Distribution Widgets */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>AT&T Messages</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].total_att_messages.toLocaleString() 
                      : '0'}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginTop: '4px' }}>{config.att_percentage}%</div>
                </div>
                
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>T-Mobile Messages</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].total_tmobile_messages.toLocaleString() 
                      : '0'}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginTop: '4px' }}>{config.tmobile_percentage}%</div>
                </div>
                
                <div style={{ 
                  backgroundColor: colors.chartBackground, 
                  border: `1px solid ${colors.border}`, 
                  borderRadius: '8px', 
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginBottom: '8px' }}>Verizon Messages</div>
                  <div style={{ color: colors.textPrimary, fontSize: '28px', fontWeight: '700' }}>
                    {(simState.phase === 'playing' || simState.phase === 'completed') && getCurrentData().length > 0 
                      ? getCurrentData()[getCurrentData().length - 1].total_verizon_messages.toLocaleString() 
                      : '0'}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '12px', marginTop: '4px' }}>{config.verizon_percentage}%</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwilioSimDashboard;
